// Consent & privacy oversight — an AUDIT TRAIL, not a control panel.
//
// TWO THINGS THIS SCREEN DELIBERATELY DOES NOT DO.
//
// 1. It never shows a child. Rows arrive from `orgConsentLog(orgId)` carrying a
//    `childRef`: an opaque, org-scoped reference that is not the child UUID and
//    cannot be resolved to one. No name, no label, no progress. An admin learns
//    that a grant exists and what it covers — nothing about the child.
//
// 2. It offers no revoke. Consent belongs to the child's parent or guardian:
//    `revoke_consent` is owner-only server-side and raises 42501 for anyone
//    else, so an org-level "Revoke" button could only ever be theatre. The
//    previous version of this screen was exactly that — it mutated a React array
//    and nothing else — which taught admins a governance model the product does
//    not have. It is replaced by a plain statement of who actually holds the
//    decision.

import { useCallback, useEffect, useState } from "react";
import { useComfort } from "../ComfortContext";
import { getDictionary } from "../../i18n/dictionary";
import { buildConsentLog } from "./data";
import { getPortalData, shouldUseSupabase } from "../../data";
import { PortalWriteError } from "../../data/supabaseAdapter";
import { useOrg } from "./OrgContext";
import type { SharingStatus } from "../../data/types";

// ---- Local strings ----------------------------------------------------------

const L = {
  en: {
    logTitle: "Consent log",
    logHint:
      "Each child appears as a reference that only means something inside your organisation. Names and progress never reach this portal.",
    colChild: "Child",
    colMember: "Member",
    colScope: "Scope",
    colStatus: "Status",
    colGranted: "Granted",
    statusActive: "Active",
    statusPending: "Awaiting consent",
    statusRevoked: "Withdrawn",
    scopeProgress: "Progress",
    scopeResults: "Results",
    scopeExport: "Export",
    withdrawnOn: "Withdrawn on",
    ownerNoteTitle: "Families hold consent, not the organisation",
    ownerNoteBody:
      "Consent is granted and withdrawn by each child's parent or guardian, in their own Zooylo app. Your organisation can see that a grant exists and what it covers, and can ask a family to review it — but nobody here can change or withdraw it.",
    loading: "Loading the consent log…",
    emptyTitle: "No consent records yet",
    emptyBody:
      "When a parent or guardian shares a child's progress with someone on your team, the grant will appear here. There is nothing for you to do until then.",
    errorTitle: "We couldn't load the consent log",
    errorNotAuthorised:
      "Your session no longer has admin access to this organisation. Please sign in again.",
    errorGeneric:
      "Something went wrong on our side, so nothing is shown rather than an empty log. Please try again in a moment.",
    retry: "Try again",
    summaryEmpty: "No records to show",
    summaryError: "The consent log could not be loaded",
    countOne: "1 consent record",
    countMany: "consent records",
    childPrefix: "Child",
  },
  ms: {
    logTitle: "Log keizinan",
    logHint:
      "Setiap kanak-kanak muncul sebagai rujukan yang hanya bermakna dalam organisasi anda. Nama dan kemajuan tidak pernah sampai ke portal ini.",
    colChild: "Kanak-kanak",
    colMember: "Ahli",
    colScope: "Skop",
    colStatus: "Status",
    colGranted: "Diberikan",
    statusActive: "Aktif",
    statusPending: "Menunggu keizinan",
    statusRevoked: "Ditarik balik",
    scopeProgress: "Kemajuan",
    scopeResults: "Keputusan",
    scopeExport: "Eksport",
    withdrawnOn: "Ditarik balik pada",
    ownerNoteTitle: "Keluarga memegang keizinan, bukan organisasi",
    ownerNoteBody:
      "Keizinan diberikan dan ditarik balik oleh ibu bapa atau penjaga kanak-kanak itu, dalam aplikasi Zooylo mereka sendiri. Organisasi anda boleh melihat bahawa keizinan wujud dan apa yang dilindunginya, dan boleh meminta keluarga menyemaknya — tetapi tiada sesiapa di sini boleh mengubah atau menariknya.",
    loading: "Memuatkan log keizinan…",
    emptyTitle: "Belum ada rekod keizinan",
    emptyBody:
      "Apabila ibu bapa atau penjaga berkongsi kemajuan kanak-kanak dengan ahli pasukan anda, keizinan itu akan muncul di sini. Tiada apa-apa untuk anda lakukan sehingga itu.",
    errorTitle: "Kami tidak dapat memuatkan log keizinan",
    errorNotAuthorised:
      "Sesi anda tidak lagi mempunyai akses pentadbir kepada organisasi ini. Sila log masuk semula.",
    errorGeneric:
      "Sesuatu tidak kena di pihak kami, jadi tiada apa-apa ditunjukkan dan bukannya log kosong. Sila cuba sebentar lagi.",
    retry: "Cuba lagi",
    summaryEmpty: "Tiada rekod untuk ditunjukkan",
    summaryError: "Log keizinan tidak dapat dimuatkan",
    countOne: "1 rekod keizinan",
    countMany: "rekod keizinan",
    childPrefix: "Kanak-kanak",
  },
} as const;

// ---- Row shape the view renders ---------------------------------------------

/** One audit row. Already formatted; the view holds no data logic. */
export interface ConsentAuditRow {
  id: string;
  /** Opaque, org-scoped reference — never a UUID, never a label. */
  childRef: string;
  memberEmail: string;
  scope: string[];
  status: SharingStatus;
  grantedLabel: string;
  withdrawnLabel?: string;
}

type LoadState = "loading" | "ready" | "error";

// ---- Table helpers ----------------------------------------------------------

const TH: React.CSSProperties = {
  padding: "10px 14px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
  borderBottom: "1px solid var(--line)",
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "14px 14px",
  fontSize: 14.5,
  color: "var(--ink)",
  borderBottom: "1px solid var(--line)",
  verticalAlign: "middle",
};

// ---- Status badge -----------------------------------------------------------

function ConsentStatusBadge({
  status,
  labels,
}: {
  status: SharingStatus;
  labels: { active: string; pending: string; revoked: string };
}) {
  const isActive = status === "active";
  const isPending = status === "pending";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: "var(--r-pill)",
        background: isActive
          ? "var(--sage-tint)"
          : isPending
            ? "var(--peach-tint)"
            : "var(--surface-sunk)",
        color: isActive
          ? "var(--sage-deep)"
          : isPending
            ? "var(--peach-deep)"
            : "var(--ink-faint)",
        fontSize: 12.5,
        fontWeight: 600,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: isActive
            ? "var(--good)"
            : isPending
              ? "var(--peach-deep)"
              : "var(--ink-faint)",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {isActive ? labels.active : isPending ? labels.pending : labels.revoked}
    </span>
  );
}

// ---- Scope chips ------------------------------------------------------------

function ScopeChips({
  scope,
  labels,
}: {
  scope: string[];
  labels: { progress: string; results: string; export: string };
}) {
  const scopeLabel: Record<string, string> = {
    progress: labels.progress,
    results: labels.results,
    export: labels.export,
  };
  if (scope.length === 0) {
    return <span style={{ color: "var(--ink-faint)", fontSize: 13.5 }}>—</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {scope.map((s) => (
        <span
          key={s}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "2px 9px",
            borderRadius: "var(--r-pill)",
            background: "var(--sky-tint)",
            color: "var(--sky-deep)",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: ".03em",
          }}
        >
          {scopeLabel[s] ?? s}
        </span>
      ))}
    </div>
  );
}

// ---- Formatting -------------------------------------------------------------

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** ISO → "17 Jun 2026". UTC so the label never shifts with the reader's clock. */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * FNV-1a over a stable per-child string → the same 6-char shape the server's
 * `childRef` uses. DEMO ONLY: it exists so the demo can show the identical
 * opaque row the live path shows, without ever rendering the fixture label it
 * hashes. The live path never calls this — its refs come from the server.
 */
function demoChildRef(seed: string): string {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).toUpperCase().padStart(6, "0").slice(0, 6);
}

// ---- Presentational view ----------------------------------------------------

export function ConsentPrivacyView({
  state,
  rows,
  errorMessage,
  onRetry,
}: {
  state: LoadState;
  rows: ConsentAuditRow[];
  errorMessage?: string;
  onRetry?: () => void;
}) {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.admin.consent;
  const l = L[locale];

  const summary =
    state === "loading"
      ? l.loading
      : state === "error"
        ? l.summaryError
        : rows.length === 0
          ? l.summaryEmpty
          : rows.length === 1
            ? l.countOne
            : `${rows.length} ${l.countMany}`;

  return (
    <>
      {/* Page header */}
      <div>
        <h1 style={{ fontSize: 30, color: "var(--ink-deep)", letterSpacing: "-.02em", margin: 0 }}>
          {t.title}
        </h1>
        <p style={{ color: "var(--ink-soft)", marginTop: 8, fontSize: 16.5, margin: "8px 0 0" }}>
          {t.subtitle}
        </p>
      </div>

      {/* Privacy banner */}
      <div
        style={{
          marginTop: 20,
          background: "var(--lilac-tint)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: "16px 18px",
          display: "flex",
          gap: 13,
          alignItems: "flex-start",
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="var(--lilac-deep)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flexShrink: 0, marginTop: 1 }}
        >
          <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />
        </svg>
        <p style={{ fontSize: 14, color: "var(--lilac-deep)", lineHeight: 1.6, margin: 0 }}>
          {t.banner}
        </p>
      </div>

      {/* Who actually holds consent — replaces the old org-level revoke override */}
      <div
        style={{
          marginTop: 12,
          padding: "14px 16px",
          background: "var(--peach-tint)",
          border: "1px solid var(--peach, var(--line))",
          borderRadius: "var(--r-md)",
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 700,
            color: "var(--peach-deep)",
          }}
        >
          {l.ownerNoteTitle}
        </p>
        <p
          style={{
            margin: "5px 0 0",
            fontSize: 13.5,
            lineHeight: 1.6,
            color: "var(--ink-soft)",
          }}
        >
          {l.ownerNoteBody}
        </p>
      </div>

      {/* Consent log */}
      <div
        style={{
          marginTop: 24,
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          boxShadow: "var(--sh-1)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "18px 20px 0" }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink-deep)", margin: 0 }}>
            {l.logTitle}
          </h2>
          <p
            style={{
              margin: "6px 0 0",
              fontSize: 13,
              lineHeight: 1.55,
              color: "var(--ink-faint)",
            }}
          >
            {l.logHint}
          </p>
        </div>

        {/* Everything below arrives asynchronously (and again after a retry), so
            the one-line summary is the live region that announces the change. */}
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: "10px 20px 0",
            fontSize: 13,
            color: "var(--ink-faint)",
          }}
        >
          {summary}
        </div>

        {state === "loading" && (
          <div style={{ padding: "16px 20px 22px" }} aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  height: 14,
                  borderRadius: 7,
                  background: "var(--surface-sunk)",
                  marginTop: i === 0 ? 0 : 12,
                  width: i === 2 ? "55%" : i === 1 ? "78%" : "92%",
                }}
              />
            ))}
          </div>
        )}

        {state === "error" && (
          <div style={{ padding: "14px 20px 22px" }}>
            <p
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 700,
                color: "var(--peach-deep)",
              }}
            >
              {l.errorTitle}
            </p>
            <p
              style={{
                margin: "6px 0 14px",
                fontSize: 14,
                lineHeight: 1.6,
                color: "var(--ink-soft)",
              }}
            >
              {errorMessage ?? l.errorGeneric}
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                style={{
                  minHeight: 44,
                  padding: "0 20px",
                  border: "1.5px solid var(--peach-deep)",
                  background: "transparent",
                  color: "var(--peach-deep)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 14,
                  borderRadius: "var(--r-pill)",
                  cursor: "pointer",
                }}
              >
                {l.retry}
              </button>
            )}
          </div>
        )}

        {state === "ready" && rows.length === 0 && (
          <div style={{ padding: "18px 20px 26px", maxWidth: 560 }}>
            <p
              style={{
                margin: 0,
                fontSize: 15.5,
                fontWeight: 700,
                color: "var(--ink-deep)",
              }}
            >
              {l.emptyTitle}
            </p>
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 14,
                lineHeight: 1.65,
                color: "var(--ink-soft)",
              }}
            >
              {l.emptyBody}
            </p>
          </div>
        )}

        {state === "ready" && rows.length > 0 && (
          <div style={{ overflowX: "auto", marginTop: 12 }}>
            <table
              style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}
              aria-label={l.logTitle}
            >
              <thead>
                <tr>
                  <th scope="col" style={TH}>{l.colChild}</th>
                  <th scope="col" style={TH}>{l.colMember}</th>
                  <th scope="col" style={TH}>{l.colScope}</th>
                  <th scope="col" style={TH}>{l.colStatus}</th>
                  <th scope="col" style={TH}>{l.colGranted}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const isLast = idx === rows.length - 1;
                  const isRevoked = row.status === "revoked";
                  const cell: React.CSSProperties = {
                    ...TD,
                    borderBottom: isLast ? "none" : TD.borderBottom,
                  };
                  return (
                    <tr key={row.id} style={{ opacity: isRevoked ? 0.6 : 1 }}>
                      <td style={cell}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "var(--ink-deep)",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {l.childPrefix} · {row.childRef}
                        </span>
                      </td>
                      <td style={cell}>{row.memberEmail}</td>
                      <td style={cell}>
                        <ScopeChips
                          scope={row.scope}
                          labels={{
                            progress: l.scopeProgress,
                            results: l.scopeResults,
                            export: l.scopeExport,
                          }}
                        />
                      </td>
                      <td style={cell}>
                        <ConsentStatusBadge
                          status={row.status}
                          labels={{
                            active: l.statusActive,
                            pending: l.statusPending,
                            revoked: l.statusRevoked,
                          }}
                        />
                        {isRevoked && row.withdrawnLabel && (
                          <div style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 3 }}>
                            {l.withdrawnOn} {row.withdrawnLabel}
                          </div>
                        )}
                      </td>
                      <td style={cell}>{row.grantedLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ---- Dispatcher + demo/live wrappers ----------------------------------------

/** Real, RLS-scoped consent audit when Supabase is configured, else the demo log. */
export function ConsentPrivacy() {
  return shouldUseSupabase() ? <ConsentPrivacyLive /> : <ConsentPrivacyDemo />;
}

function ConsentPrivacyDemo() {
  // Fixture-backed, but rendered through the SAME opaque shape the live path
  // uses — a demo that showed child labels would teach a model the product does
  // not have. `childLabel` is hashed here and never reaches the DOM.
  const [rows] = useState<ConsentAuditRow[]>(() =>
    buildConsentLog().map((e) => ({
      id: e.id,
      childRef: demoChildRef(e.childLabel),
      memberEmail: e.memberEmail,
      scope: e.scope,
      status: e.status,
      grantedLabel: e.grantedLabel,
      withdrawnLabel: e.revokedLabel,
    })),
  );
  return <ConsentPrivacyView state="ready" rows={rows} />;
}

/** Maps a read failure to calm copy, branching on SQLSTATE — never message text. */
function readErrorCopy(err: unknown, l: (typeof L)["en"] | (typeof L)["ms"]): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  if (code === "42501") return l.errorNotAuthorised;
  return l.errorGeneric;
}

function ConsentPrivacyLive() {
  const { locale } = useComfort();
  const l = L[locale];
  // orgId is resolved once by OrgProvider and guaranteed real here: AuthGate
  // renders the admin shell only for status "admin". Never re-derive it.
  const { orgId } = useOrg();

  const [state, setState] = useState<LoadState>("loading");
  const [rows, setRows] = useState<ConsentAuditRow[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [nonce, setNonce] = useState(0);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setErrorMessage("");

    (async () => {
      try {
        const entries = await getPortalData().orgConsentLog(orgId);
        if (cancelled) return;
        setRows(
          entries.map((e) => ({
            id: e.consentId,
            childRef: e.childRef,
            memberEmail: e.memberEmail,
            scope: e.scope as string[],
            status: e.status,
            grantedLabel: fmtDate(e.grantedAt),
            withdrawnLabel: e.revokedAt ? fmtDate(e.revokedAt) : undefined,
          })),
        );
        setState("ready");
      } catch (err) {
        if (cancelled) return;
        // A read failure must never look like "no consents exist".
        setRows([]);
        setErrorMessage(readErrorCopy(err, l));
        setState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
    // `l` is a static per-locale constant; excluding it keeps a locale switch
    // from re-issuing the network read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, nonce]);

  return (
    <ConsentPrivacyView
      state={state}
      rows={rows}
      errorMessage={errorMessage}
      onRetry={retry}
    />
  );
}
